declare global {
  interface Window {
    cloudchatSDK: { run: (options: { websiteToken: string; baseUrl: string }) => void };
    cloudchatSettings?: {
      hideMessageBubble?: boolean;
      showUnreadMessagesDialog?: boolean;
    };
    $cloudchat: {
      locale?: string;
      position?: string;
      hideMessageBubble?: boolean;
      showPopoutButton?: boolean;
      widgetStyle?: string;
      darkMode?: string;
      setUser: (email: string, attrs: { email: string; name?: string | null; phone_number?: string | null }) => void;
      setCustomAttributes: (attrs: Record<string, string>) => void;
      toggle: (state?: "open" | "close") => void;
      toggleBubbleVisibility: (state: "show" | "hide") => void;
    };
  }
}

export const CLOUD_HUMANS_BASE_URL = "https://cloudchat2.cloudhumans.com";
export const CLOUD_HUMANS_WEBSITE_TOKEN = "oTRE8au76T4ZQfrKvB8rVj7t";

let isReady = false;
const readyListeners = new Set<() => void>();

export const markCloudHumansReady = () => {
  if (isReady) return;
  isReady = true;
  readyListeners.forEach((listener) => listener());
};

export const getCloudHumansReady = () => isReady;

export const subscribeCloudHumansReady = (listener: () => void) => {
  readyListeners.add(listener);
  return () => {
    readyListeners.delete(listener);
  };
};

export const openCloudHumansChat = () => {
  window.$cloudchat?.toggle("open");
};

export const setCloudHumansBubbleVisible = (isVisible: boolean) => {
  if (!isReady) return;
  window.$cloudchat?.toggleBubbleVisibility(isVisible ? "show" : "hide");
};

const MESSAGE_PREFIX = "cloudchat-widget:";
const UNREAD_EVENT = "handleNotificationDot";
const LOADED_EVENT = "loaded";
const WIDGET_FRAME_ID = "chatwoot_live_chat_widget";
const CHAT_ORIGIN = new URL(CLOUD_HUMANS_BASE_URL).origin;

let unreadCount = 0;
const unreadListeners = new Set<() => void>();

export const getCloudHumansUnreadCount = () => unreadCount;

export const subscribeCloudHumansUnreadCount = (listener: () => void) => {
  unreadListeners.add(listener);
  return () => {
    unreadListeners.delete(listener);
  };
};

let channelMultipleConversations: boolean | undefined;

export const listenCloudHumansWidget = () => {
  const handleMessage = (event: MessageEvent) => {
    if (event.origin !== CHAT_ORIGIN) return;
    if (typeof event.data !== "string" || !event.data.startsWith(MESSAGE_PREFIX)) {
      return;
    }

    let payload: { event?: unknown; unreadMessageCount?: unknown; config?: unknown };
    try {
      payload = JSON.parse(event.data.slice(MESSAGE_PREFIX.length));
    } catch {
      return;
    }

    if (payload?.event === LOADED_EVENT) {
      const config = (payload as { config?: { channelConfig?: unknown } }).config;
      const channel = config?.channelConfig as
        | { multipleConversations?: unknown }
        | undefined;
      if (typeof channel?.multipleConversations === "boolean") {
        channelMultipleConversations = channel.multipleConversations;
      }
      return;
    }

    if (payload?.event !== UNREAD_EVENT) return;

    const count = payload.unreadMessageCount;
    if (typeof count !== "number" || !Number.isFinite(count)) return;

    const next = Math.max(0, Math.trunc(count));
    if (next === unreadCount) return;

    unreadCount = next;
    unreadListeners.forEach((listener) => listener());
  };

  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
};

const PANEL_SELECTOR = ".woot-widget-holder";
const PANEL_SHADOW = "0 5px 40px rgb(0 0 0 / 0.16)";
const DESKTOP_QUERY = "(min-width: 667px)";

export const enforceCloudHumansPanelStyle = () => {
  const panel = document.querySelector<HTMLElement>(PANEL_SELECTOR);
  if (!panel) return () => {};

  const apply = () => {
    if (panel.classList.contains("has-unread-view")) {
      panel.style.removeProperty("box-shadow");
      panel.style.removeProperty("bottom");
      return;
    }

    panel.style.setProperty("box-shadow", PANEL_SHADOW, "important");

    const isFloating = window.matchMedia(DESKTOP_QUERY).matches;
    if (isFloating && panel.classList.contains("woot-widget--without-bubble")) {
      panel.style.setProperty("bottom", "104px", "important");
    } else {
      panel.style.removeProperty("bottom");
    }
  };

  apply();

  const observer = new MutationObserver(apply);
  observer.observe(panel, { attributes: true, attributeFilter: ["class"] });

  const media = window.matchMedia(DESKTOP_QUERY);
  media.addEventListener("change", apply);

  return () => {
    observer.disconnect();
    media.removeEventListener("change", apply);
  };
};

const readCookie = (name: string) =>
  document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

export const setCloudHumansUnreadPreview = (isEnabled: boolean) => {
  const chat = window.$cloudchat;
  const frame = document.getElementById(
    WIDGET_FRAME_ID,
  ) as HTMLIFrameElement | null;
  if (!isReady || !chat || !frame?.contentWindow) return;

  const config = {
    event: "config-set",
    locale: chat.locale,
    position: chat.position,
    hideMessageBubble: chat.hideMessageBubble,
    showPopoutButton: chat.showPopoutButton,
    widgetStyle: chat.widgetStyle,
    darkMode: chat.darkMode,
    showUnreadMessagesDialog: isEnabled,
    multipleConversations: channelMultipleConversations,
    campaignsSnoozedTill: readCookie("cw_snooze_campaigns_till"),
  };

  frame.contentWindow.postMessage(
    `${MESSAGE_PREFIX}${JSON.stringify(config)}`,
    CHAT_ORIGIN,
  );
};
